import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { SpotRatesAllCurrSyncService } from './spotrates-all-curr-sync.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'gtw-spotrates-all-curr-sync',
  templateUrl: './spotrates-all-curr-sync.component.html',
  styleUrls: ['./spotrates-all-curr-sync.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SpotratesAllCurrSyncComponent implements OnInit, OnDestroy {
  /** Legacy AngularJS bridge (spotRatesAllCurrSyncCtrl). */
  @Output('close-currconfirm-info')
  closeCurrconfirmInfo = new EventEmitter<string>();

  @Output('save-currsync-info')
  saveCurrsyncInfo = new EventEmitter<string>();

  /** Consumed by CeHostDialogComponent in the migrated grid flow. */
  @Output('close-modal')
  closeModal = new EventEmitter<string>();

  @Output('save-and-close')
  saveAndClose = new EventEmitter<any>();

  @Output('request-grid-update')
  requestGridUpdate = new EventEmitter<string>();

  showModal = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to sync the currencies?',
    iconClass: 'Appkit4-icon icon-logout-fill',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'Cancel',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  baseGlobalParams: {
    jcd_key?: string | number;
    tax_year?: string | number;
    scenario?: string | number;
    client_key?: string | number;
    scenarioCode?: string;
    checkLockedbj?: any;
  } | undefined;

  baseURLs: any = { admin: '', api: '/gtw', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };

  private taxYear = 0;
  private scenario = 0;
  private jcdKey = 0;
  private screenKey = 0;
  private ssoId = '';
  private checkLockedObj: any = [];
  private saveRequestSeq = 0;

  subscriptions: Subscription = new Subscription();

  constructor(
    private spotRatesAllCurrSyncService: SpotRatesAllCurrSyncService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  checkInputType(): boolean {
    return (
      Number.isInteger(parseInt(this.taxYear + '', 10)) &&
      Number.isInteger(parseInt(this.screenKey + '', 10)) &&
      Number.isInteger(parseInt(this.scenario + '', 10)) &&
      Number.isInteger(parseInt(this.jcdKey + '', 10))
    );
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      }),
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = globalParams;
    this.taxYear = Number(globalParams?.tax_year || this.taxYear || 0);
    this.scenario = Number(globalParams?.scenario || this.scenario || 0);
    this.jcdKey = Number(globalParams?.jcd_key || this.jcdKey || 0);
    this.screenKey = Number(globalParams?.screen_key || this.screenKey || 0);
    const sso = this.gtwApp?.USER_SETTINGS?.user?.sso_id;
    this.ssoId = sso != null && sso !== '' ? String(sso) : this.ssoId;
    this.checkLockedObj = globalParams?.checkLockedbj || this.checkLockedObj;
    this.cdr.markForCheck();
  }

  init(): void {
    if (this.checkInputType()) {
      // No-op; placeholder retained for parity with AngularJS controller.
    }
  }

  onCancel(): void {
    this.showModal = false;
    this.closeCurrconfirmInfo.emit('close-currconfirm-info');
    this.closeModal.emit('close-modal');
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.markForCheck();

    const tempData = {
      TAX_YEAR: this.baseGlobalParams?.tax_year,
      SCENARIO: this.baseGlobalParams?.scenario,
      JCD_KEY: this.baseGlobalParams?.jcd_key,
      SSO_ID: this.ssoId,
    };

    const seq = ++this.saveRequestSeq;
    const updateSubscription = this.spotRatesAllCurrSyncService
      .syncCurrData(this.baseURLs.api, tempData, this.baseGlobalParams, this.checkLockedObj)
      .subscribe(
        (data: any) => {
          if (seq !== this.saveRequestSeq) {
            return;
          }
          if (data.callSuccess == '1') {
            this.spotRatesAllCurrSyncService.openSnackBar(
              'Data Synced Successfully',
              'gtw-snackbar--green',
            );
            this.saveCurrsyncInfo.emit('save-currsync-info');
            this.saveAndClose.emit('save-and-close');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.spotRatesAllCurrSyncService.openSnackBar(
              'Error syncing Data. ' + data.errorMessage,
              'gtw-snackbar--red',
            );
            this.modalConfig = { ...this.modalConfig, loading: false };
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          if (seq !== this.saveRequestSeq) {
            return;
          }
          this.spotRatesAllCurrSyncService.openSnackBar(
            'Error syncing Data. ' + error?.error?.errorMessage,
            'gtw-snackbar--red',
          );
          this.modalConfig = { ...this.modalConfig, loading: false };
          this.cdr.markForCheck();
        },
      );

    this.subscriptions.add(updateSubscription);
  }
}
